import type {Repository} from '@github-ui/agent-sessions-interface/components/repo-picker/repo'
import {reactFetchJSON} from '@github-ui/verified-fetch'

const REPO_RESULTS_LIMIT = 10
const REPOSITORIES_SEARCH_PATH = '/github-copilot/chat/repositories_search'

// Raw repository shape returned by the dotcom `repositories_search` endpoint.
// Only the fields the picker consumes are typed here.
interface RawRepository {
  databaseId: number
  nameWithOwner: string
  isPrivate?: boolean
}

function transformRepository(raw: RawRepository): Repository {
  return {
    id: raw.databaseId,
    name: raw.nameWithOwner,
    private: raw.isPrivate ?? false,
  }
}

/**
 * Fetches repositories for the repo picker from the same-origin dotcom
 * `repositories_search` endpoint via the React JSON fetch helper. This
 * deliberately bypasses copilot-chat's chat service/manager — it issues its own
 * request so the picker carries no dependency on copilot-chat. An empty `query`
 * returns the server's owner-prioritized default list.
 */
export async function fetchRepos(query: string, signal?: AbortSignal): Promise<Repository[]> {
  const trimmedQuery = query.trim()
  const params = new URLSearchParams()
  if (trimmedQuery) params.set('q', trimmedQuery)
  params.set('limit', String(REPO_RESULTS_LIMIT))

  const response = await reactFetchJSON(`${REPOSITORIES_SEARCH_PATH}?${params.toString()}`, {
    method: 'GET',
    signal,
  })

  if (!response.ok) {
    throw new Error(`Failed to load repositories (${response.status})`)
  }

  const data = (await response.json()) as {repositories?: RawRepository[]}
  return (data.repositories ?? []).map(transformRepository)
}
